/* Veloura core: shared helpers, cart API, product form, cart drawer, cart items,
   quantity input, dialogs, and scroll reveal. No dependencies. */
(() => {
  const V = (window.Veloura = window.Veloura || {});
  V.routes = V.routes || {};
  V.settings = V.settings || {};
  V.strings = V.strings || {};

  /* ---------- Helpers ---------- */
  V.announce = (message) => {
    const region = document.getElementById('A11yStatus');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  };

  V.debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  V.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  V.fetchSection = async (url, sectionId, signal) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target.toString(), { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(String(response.status));
    return V.parseHTML(await response.text());
  };

  V.setBusy = (button, busy) => {
    if (!button) return;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
    }
  };

  /* Replaces matching [data-region] elements inside `scope` with those from `doc`. */
  V.swapRegions = (doc, scope = document) => {
    const active = document.activeElement;
    const activeKey = active && active.closest('[data-focus-key]') ? active.closest('[data-focus-key]').dataset.focusKey : null;
    scope.querySelectorAll('[data-region]').forEach((current) => {
      const next = doc.querySelector(`[data-region="${current.dataset.region}"]`);
      if (next) current.replaceWith(next);
    });
    if (activeKey) {
      const restored = document.querySelector(`[data-focus-key="${CSS.escape(activeKey)}"]`);
      if (restored) restored.focus({ preventScroll: true });
    }
  };

  /* ---------- Dialogs ---------- */
  V.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.veloOpener = opener || document.activeElement;
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
  };

  V.closeDialog = (dialog) => {
    if (dialog && dialog.open) dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.dialogOpen);
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        V.openDialog(dialog, opener);
        return;
      }
    }

    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      event.preventDefault();
      V.closeDialog(closer.closest('dialog'));
      return;
    }

    /* Appointment buttons link to #AppointmentDialog while no appointment page is set. */
    const appointmentLink = event.target.closest('a[href*="#AppointmentDialog"]');
    if (appointmentLink) {
      const dialog = document.getElementById('AppointmentDialog');
      if (dialog && typeof dialog.showModal === 'function') {
        event.preventDefault();
        const hash = appointmentLink.getAttribute('href').split('#AppointmentDialog')[1] || '';
        const service = new URLSearchParams(hash.replace(/^\?/, '')).get('service');
        const field = dialog.querySelector('[data-appointment-service]');
        if (field && service) field.value = service;
        V.openDialog(dialog, appointmentLink);
        const first = dialog.querySelector('input:not([type="hidden"])');
        if (first) first.focus({ preventScroll: true });
      }
    }

    if (event.target instanceof HTMLDialogElement && event.target.open && event.target.hasAttribute('data-light-dismiss')) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) V.closeDialog(event.target);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      const opener = dialog.veloOpener;
      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
      dialog.veloOpener = null;
    },
    true
  );

  /* Re-opens a dialog that rendered a form result (success or errors) after a submission. */
  const reopenAfterPost = () => {
    const status = document.querySelector('dialog [data-dialog-auto-open]');
    if (!status) return;
    const dialog = status.closest('dialog');
    if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
      V.openDialog(dialog);
      status.focus({ preventScroll: true });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reopenAfterPost);
  else reopenAfterPost();

  /* ---------- Cart API ---------- */
  const jsonUrl = (url) => `${url}.js`;

  V.cart = {
    sectionIds() {
      return [...new Set([...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection))];
    },

    async request(url, body) {
      const sections = this.sectionIds();
      let options;
      if (body instanceof FormData) {
        if (sections.length) {
          body.append('sections', sections.join(','));
          body.append('sections_url', window.location.pathname);
        }
        options = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body };
      } else {
        const payload = Object.assign({}, body);
        if (sections.length) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }
        options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        };
      }

      const response = await fetch(url, options);
      let data = {};
      try {
        data = await response.json();
      } catch (error) {
        data = {};
      }
      if (!response.ok || data.status) {
        const failure = new Error(data.description || data.message || V.strings.cartError);
        failure.data = data;
        throw failure;
      }
      return data;
    },

    add(formData) {
      return this.request(jsonUrl(V.routes.cartAdd), formData);
    },

    change(key, quantity) {
      return this.request(jsonUrl(V.routes.cartChange), { id: key, quantity });
    },

    async updateNote(note) {
      const response = await fetch(jsonUrl(V.routes.cartUpdate), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note }),
      });
      if (!response.ok) throw new Error(V.strings.cartError);
      return response.json();
    },

    render(sections) {
      if (!sections) return;
      let count = null;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = V.parseHTML(html);
        document.querySelectorAll(`[data-cart-section="${id}"]`).forEach((root) => V.swapRegions(doc, root));
        const source = doc.querySelector('[data-cart-item-count]');
        if (source) count = Number(source.dataset.cartItemCount);
      });
      if (count !== null) this.updateCount(count);
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = count === 0;
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count } }));
    },
  };

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', this.onClick);
      this.input.addEventListener('input', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const button = event.target.closest('[data-step]');
      if (!button || button.disabled) return;
      event.preventDefault();
      const step = Number(this.input.step) || 1;
      const min = this.input.min === '' ? 0 : Number(this.input.min);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const current = Number(this.input.value) || 0;
      const next = Math.min(max, Math.max(min, current + step * Number(button.dataset.step)));
      if (next === current) return;
      this.input.value = next;
      this.sync();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    sync = () => {
      const value = Number(this.input.value) || 0;
      const min = this.input.min === '' ? 0 : Number(this.input.min);
      const max = this.input.max === '' ? Infinity : Number(this.input.max);
      const minus = this.querySelector('[data-step="-1"]');
      const plus = this.querySelector('[data-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    };
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Product form ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    get errorRegion() {
      return this.querySelector('[data-form-error]');
    }

    showError(message) {
      const region = this.errorRegion;
      if (!region) return;
      const text = region.querySelector('[data-form-error-text]');
      if (text) text.textContent = message;
      region.hidden = !message;
    }

    onSubmit = async (event) => {
      const useAjax = V.settings.cartType === 'drawer' && !V.settings.isCartPage && document.getElementById('CartDrawer');
      if (!useAjax) return;
      event.preventDefault();

      const submitter = event.submitter || this.form.querySelector('[type="submit"]');
      if (submitter && submitter.getAttribute('aria-busy') === 'true') return;
      if (submitter && submitter.getAttribute('aria-disabled') === 'true') return;

      this.showError('');
      V.setBusy(submitter, true);
      const formData = new FormData(this.form);

      try {
        const data = await V.cart.add(formData);
        V.cart.render(data.sections);
        const drawer = document.getElementById('CartDrawer');
        V.openDialog(drawer, submitter);
        V.announce(V.strings.addedToCart);
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data } }));
      } catch (error) {
        this.showError(error.message || V.strings.cartError);
        V.announce(error.message || V.strings.cartError);
      } finally {
        V.setBusy(submitter, false);
      }
    };
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* ---------- Cart items (drawer and page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.queue = V.debounce((key, quantity) => this.update(key, quantity), 350);
    }

    onKeydown = (event) => {
      if (event.key !== 'Enter' || !event.target.matches('[data-line-quantity]')) return;
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
    };

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    onChange = (event) => {
      const input = event.target.closest('[data-line-quantity]');
      if (input) {
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.queue(input.dataset.key, quantity);
        return;
      }
      const note = event.target.closest('[data-cart-note]');
      if (note) {
        V.cart.updateNote(note.value).catch(() => V.announce(V.strings.cartError));
      }
    };

    onClick = (event) => {
      const remove = event.target.closest('[data-line-remove]');
      if (!remove) return;
      event.preventDefault();
      this.update(remove.dataset.key, 0);
    };

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    restoreFocus(root) {
      const active = document.activeElement;
      if (active && active !== document.body && active.isConnected) return;
      const target = root && root.querySelector('[data-cart-focus]');
      if (target) target.focus({ preventScroll: true });
    }

    async update(key, quantity) {
      if (!key) return;
      const root = this.closest('[data-cart-section]');
      this.setBusy(true);
      try {
        const data = await V.cart.change(key, quantity);
        V.cart.render(data.sections);
        V.cart.updateCount(data.item_count);
        this.restoreFocus(root);
        const line = (data.items || []).find((item) => item.key === key);
        if (quantity > 0 && line && line.quantity < quantity) {
          const message = V.strings.quantityLimited.replace('[quantity]', line.quantity);
          this.showErrorAfterRender(message);
        } else {
          V.announce(V.strings.cartUpdated);
        }
      } catch (error) {
        this.showErrorAfterRender(error.message || V.strings.cartError);
      } finally {
        this.setBusy(false);
        this.restoreFocus(root);
      }
    }

    showErrorAfterRender(message) {
      document.querySelectorAll('[data-cart-error]').forEach((region) => {
        region.textContent = message;
        region.hidden = false;
      });
      V.announce(message);
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  /* ---------- Cart drawer trigger ---------- */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cart-trigger]');
    if (!trigger) return;
    const drawer = document.getElementById('CartDrawer');
    if (V.settings.cartType !== 'drawer' || V.settings.isCartPage || !drawer) return;
    event.preventDefault();
    V.openDialog(drawer, trigger);
  });

  /* ---------- Scroll reveal ---------- */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  V.observeReveal = (scope = document) => {
    if (!revealObserver || !document.documentElement.classList.contains('animations-reveal')) return;
    scope.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => revealObserver.observe(el));
  };

  const initReveal = () => {
    V.revealReady = true;
    V.observeReveal();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }

  document.addEventListener('shopify:section:load', (event) => V.observeReveal(event.target));
})();
